import { useTranslation } from 'react-i18next';
import {
  CitationChip,
  normalizeString,
  NumberInput,
  SingleSelect,
} from '@asset-metrix/portal-ui';

import {
  AggregationMethod,
  LimitMode,
  LimitParameter,
  OperatorValue,
  operatorValueOptions,
  POSITION_KPI,
} from './createInvestmentLimitModel';
import { getAvailableKpis } from './investmentLimitKpiAvailability';
import {
  aggregationMethodSelectData,
  dataFieldLabel,
  dataFieldSelectData,
  expressionValueSuffix,
} from './investmentLimitLabels';

import styles from './createInvestmentLimitModal.module.scss';

export type LimitExpressionFieldsProps = {
  limitMode: LimitMode;
  onChange: (update: Partial<LimitParameter>) => void;
  parameter: LimitParameter;
};

function numberInputValue(value: string | number) {
  return typeof value === 'number' ? String(value) : value;
}

function StepHeading({ label, step }: { label: string; step: number }) {
  return (
    <h3 className={styles['step-heading']}>
      <CitationChip>{step}</CitationChip>
      <span>{label}</span>
    </h3>
  );
}

export function LimitExpressionFields({
  limitMode,
  onChange,
  parameter,
}: LimitExpressionFieldsProps) {
  const { t } = useTranslation();
  const isPositionKpi = limitMode === 'Position';
  const availableKpis = dataFieldSelectData(t, getAvailableKpis(parameter));
  const kpiSelectionDisabled =
    normalizeString(parameter.dataLevelId) == null ||
    (parameter.valueType === 'relative' &&
      normalizeString(parameter.denominator?.dataLevelId) == null);
  const limitComparison = (
    <>
      <div className={styles['rule-formula-segment-field']}>
        <SingleSelect
          clearable={false}
          data={
            isPositionKpi
              ? [
                  {
                    label: dataFieldLabel(t, POSITION_KPI),
                    value: POSITION_KPI,
                  },
                ]
              : availableKpis
          }
          disabled={isPositionKpi || kpiSelectionDisabled}
          label={t('app.investmentLimit.fields.kpi')}
          onChange={(value) => onChange({ dataField: value })}
          placeholder={
            !isPositionKpi && kpiSelectionDisabled
              ? t('app.investmentLimit.fields.requiresDataLevel')
              : t('app.investmentLimit.fields.selectKpi')
          }
          required
          value={parameter.dataField}
        />
      </div>
      <div className={styles['rule-formula-segment-operator']}>
        <SingleSelect<OperatorValue>
          clearable={false}
          data={operatorValueOptions}
          label={t('app.investmentLimit.fields.limitOperator')}
          onChange={(value) => onChange({ ruleOperator: value })}
          placeholder={t('app.investmentLimit.fields.operatorPlaceholder')}
          required
          value={parameter.ruleOperator}
        />
      </div>
      <div className={styles['rule-formula-segment-value']}>
        <NumberInput
          label={t('app.investmentLimit.fields.limitValue')}
          onChange={(value) =>
            onChange({ limitValue: numberInputValue(value) })
          }
          placeholder={t('app.investmentLimit.fields.valuePlaceholder')}
          required
          suffix={expressionValueSuffix(
            limitMode,
            parameter.valueType,
            'limitValue'
          )}
          value={parameter.limitValue}
        />
      </div>
      <div className={styles['rule-formula-segment-warning']}>
        <NumberInput
          label={t('app.investmentLimit.fields.warningThreshold')}
          onChange={(value) =>
            onChange({ warningThreshold: numberInputValue(value) })
          }
          placeholder={t('app.investmentLimit.fields.warningPlaceholder')}
          suffix="%"
          value={parameter.warningThreshold}
        />
      </div>
    </>
  );

  const aggregationNumberDisabled = parameter.aggregationMethod === 'Sum';

  return (
    <div className={styles['parameter-section']}>
      {limitMode === 'Aggregation' && (
        <div className={styles['evaluation-step']}>
          <StepHeading
            label={t('app.investmentLimit.preview.topLevelAggregation')}
            step={1}
          />
          <div className={styles['form-grid']}>
            <SingleSelect<AggregationMethod>
              clearable={false}
              data={aggregationMethodSelectData(t)}
              label={t('app.investmentLimit.fields.aggregationMethod')}
              onChange={(value) => onChange({ aggregationMethod: value })}
              placeholder={t('app.investmentLimit.fields.selectMethod')}
              required
              value={parameter.aggregationMethod}
            />
            <NumberInput
              allowDecimal={false}
              disabled={aggregationNumberDisabled}
              label={t('app.investmentLimit.fields.aggregationNumber')}
              min={1}
              onChange={(value) =>
                onChange({ aggregationNumber: numberInputValue(value) })
              }
              placeholder={t(
                'app.investmentLimit.fields.aggregationNumberPlaceholder'
              )}
              required={
                parameter.aggregationMethod === 'Top N' ||
                parameter.aggregationMethod === 'Bottom N'
              }
              value={parameter.aggregationNumber}
            />
          </div>
        </div>
      )}
      {limitMode === 'Position' && (
        <div className={styles['evaluation-step']}>
          <StepHeading
            label={t('app.investmentLimit.preview.singlePositionExpression')}
            step={1}
          />
          <div
            className={styles['rule-formula']}
            aria-label={t(
              'app.investmentLimit.preview.singlePositionExpression'
            )}
          >
            <div className={styles['rule-formula-segment-field']}>
              <SingleSelect
                clearable={false}
                data={availableKpis}
                disabled={kpiSelectionDisabled}
                label={t('app.investmentLimit.fields.evaluationMetric')}
                onChange={(value) => onChange({ evalMetric: value })}
                placeholder={
                  kpiSelectionDisabled
                    ? t('app.investmentLimit.fields.requiresDataLevel')
                    : t('app.investmentLimit.fields.selectMetric')
                }
                required
                value={parameter.evalMetric}
              />
            </div>
            <div className={styles['rule-formula-segment-operator']}>
              <SingleSelect<OperatorValue>
                clearable={false}
                data={operatorValueOptions}
                label={t('app.investmentLimit.fields.evaluationOperator')}
                onChange={(value) => onChange({ evalOperator: value })}
                placeholder={t(
                  'app.investmentLimit.fields.operatorPlaceholder'
                )}
                required
                value={parameter.evalOperator}
              />
            </div>
            <div className={styles['rule-formula-segment-value']}>
              <NumberInput
                label={t('app.investmentLimit.fields.evaluationValue')}
                onChange={(value) =>
                  onChange({ evalValue: numberInputValue(value) })
                }
                placeholder={t('app.investmentLimit.fields.valuePlaceholder')}
                required
                suffix={expressionValueSuffix(
                  limitMode,
                  parameter.valueType,
                  'evalValue'
                )}
                value={parameter.evalValue}
              />
            </div>
          </div>
        </div>
      )}
      {(limitMode === 'Aggregation' || limitMode === 'Position') && (
        <div className={styles['evaluation-step']}>
          <StepHeading
            label={t('app.investmentLimit.preview.kpiLimitComparison')}
            step={2}
          />
          <div
            className={styles['rule-formula']}
            aria-label={t('app.investmentLimit.preview.kpiLimitComparison')}
          >
            {limitComparison}
          </div>
        </div>
      )}
    </div>
  );
}
